import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/apiRouting';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AskBSOProps {
  getToken?: () => Promise<string | null>;
  storeId?: string | null;
  enterpriseId?: string;
  onClose?: () => void;
}

const AskBSO: React.FC<AskBSOProps> = ({ getToken, storeId, enterpriseId, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hi, I am BSO Analytics. Ask me anything about your restaurant performance -- revenue trends, team stats, review insights, and more.',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const token = getToken ? await getToken() : null;
      const response = await fetch(API_ENDPOINTS.askBsoQuery(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question,
          storeId,
          enterpriseId,
          context: {
            previousMessages: messages.slice(-4).map((m) => ({
              role: m.role,
              content: m.content,
            })),
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.answer || data.response || data.message || 'I was unable to process that question. Please try again.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: 'I encountered an error processing your request. Please try again in a moment.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (err) {
      console.error('Error querying Ask BSO:', err);
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: 'Unable to connect to the analytics service. Please check your connection and try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="cc-ask-bso">
      <div className="cc-ask-bso__messages">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`cc-ask-bso__message ${
              msg.role === 'user' ? 'cc-ask-bso__message--user' : 'cc-ask-bso__message--assistant'
            }`}
          >
            <div className="cc-ask-bso__message-icon">
              {msg.role === 'user' ? (
                <User size={14} />
              ) : (
                <Bot size={14} />
              )}
            </div>
            <div className="cc-ask-bso__message-content">
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="cc-ask-bso__message cc-ask-bso__message--assistant">
            <div className="cc-ask-bso__message-icon">
              <Bot size={14} />
            </div>
            <div className="cc-ask-bso__message-content cc-ask-bso__typing">
              <Loader size={14} className="cc-spin" />
              <span>Analyzing...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="cc-ask-bso__input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="cc-ask-bso__input"
          placeholder="Ask about revenue, reviews, team performance..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          className="cc-ask-bso__send"
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default AskBSO;
